if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}
/**
 *  @class
 *  @function ThemeHeader
 */

if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.announcement_section = document.querySelector('.announcement-bar-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = this.querySelector('.mobile-toggle-wrapper');
      this.lastScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      this.scrollDeltaThreshold = 28;
      this.scrollRaf = null;
      this.lastVisibilityToggleAt = 0;
      this.visibilityCooldownMs = 260;
      this.mobileBreakpoint = 1068;
      this.announcementHeight = 36;
      this.headerHeight = 60;
      this.scrollIntent = 0;
      this.lastScrollDirection = 0;

      document.addEventListener('keyup', (e) => {
        if (e.code) {
          if (e.code.toUpperCase() === 'ESCAPE') {
            this.toggle.removeAttribute('open');
            this.toggle.classList.remove('active');
          }
        }
      });
      if (this.classList.contains('header-sticky--active')) {
        document.body.classList.add('header-sticky--active');
      }
      this.toggle.querySelector('.mobile-toggle').addEventListener('click', (e) => {
        if (this.toggle.classList.contains('active')) {
          e.preventDefault();
          document.body.classList.remove('overflow-hidden');
          this.toggle.classList.remove('active');
          if (this.header_section) this.header_section.classList.remove('menu-open');
          this.closeAnimation(this.toggle);
        } else {
          document.body.classList.add('overflow-hidden');
          this.setScrollHiddenState(false);
          if (this.header_section) this.header_section.classList.add('menu-open');
          setTimeout(() => {
            this.toggle.classList.add('active');
          });
        }
        window.dispatchEvent(new Event('resize.resize-select'));
      });

      // Sticky behavior + hide/reveal + offsets in a single RAF scroll loop
      window.addEventListener('scroll', this.onScroll.bind(this), {
        passive: true
      });
      window.addEventListener('resize', this.onResize.bind(this), {
        passive: true
      });

      window.dispatchEvent(new Event('scroll'));

      if (this.announcement_section) {
        this.setAnnouncementHeight();
        window.dispatchEvent(new Event('resize'));
      }

      // Buttons.
      this.menu.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
    }
    onScroll() {
      if (this.scrollRaf) return;
      this.scrollRaf = requestAnimationFrame(() => {
        this.setStickyClass();
        this.setAnnouncementHeight();
        this.setHeaderHeight();
        this.setHeaderGroupHeight();
        this.setHeaderOffset();
        this.scrollRaf = null;
      });
    }
    onResize() {
      if (window.innerWidth >= this.mobileBreakpoint) {
        this.setScrollHiddenState(false);
      }
      this.setAnnouncementHeight();
      this.onScroll();
    }
    setScrollHiddenState(hidden) {
      this.classList.toggle('is-scroll-hidden', hidden);
      if (this.header_section) {
        this.header_section.classList.toggle('is-scroll-hidden', hidden);
      }
      if (this.announcement_section) {
        this.announcement_section.classList.toggle('is-scroll-hidden', hidden);
      }
    }
    setStickyClass() {
      if (this.classList.contains('header-sticky--active')) {
        let h = this.getBoundingClientRect().top;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        const delta = scrollTop - this.lastScrollTop;
        const isMenuOpen = this.toggle && this.toggle.classList.contains('active');
        const isSearchOpen = this.header_section && this.header_section.classList.contains('search-open');
        const hideStart = this.offsetHeight + 20;
        const isMobileViewport = window.innerWidth < this.mobileBreakpoint;

        if (h <= 0) {
          this.classList.add('is-sticky');
        } else {
          this.classList.remove('is-sticky');
        }

        if (this.header_section) {
          if (!isMobileViewport) {
            this.setScrollHiddenState(false);
            this.lastScrollTop = Math.max(0, scrollTop);
            return;
          }
          if (isMenuOpen || isSearchOpen || scrollTop <= hideStart) {
            this.setScrollHiddenState(false);
            this.scrollIntent = 0;
            this.lastScrollDirection = 0;
          } else {
            const direction = delta > 0 ? 1 : (delta < 0 ? -1 : 0);
            if (direction === 0) {
              this.lastScrollTop = Math.max(0, scrollTop);
              return;
            }

            if (direction !== this.lastScrollDirection) {
              this.scrollIntent = 0;
              this.lastScrollDirection = direction;
            }

            this.scrollIntent += Math.abs(delta);
            if (this.scrollIntent < this.scrollDeltaThreshold) {
              this.lastScrollTop = Math.max(0, scrollTop);
              return;
            }

            const now = Date.now();
            if (now - this.lastVisibilityToggleAt < this.visibilityCooldownMs) {
              this.lastScrollTop = Math.max(0, scrollTop);
              return;
            }

            if (direction > 0 && !this.classList.contains('is-scroll-hidden')) {
              this.setScrollHiddenState(true);
              this.lastVisibilityToggleAt = now;
              this.scrollIntent = 0;
            } else if (direction < 0 && this.classList.contains('is-scroll-hidden')) {
              this.setScrollHiddenState(false);
              this.lastVisibilityToggleAt = now;
              this.scrollIntent = 0;
            }
          }
        }
        this.lastScrollTop = Math.max(0, scrollTop);
      }
    }
    setAnnouncementHeight() {
      const a_bar = this.announcement_section || document.querySelector('.announcement-bar-section');
      if (!a_bar) return;
      const a_bar_inner = a_bar.querySelector('.announcement-bar');
      const measured = a_bar_inner ? Math.round(a_bar_inner.getBoundingClientRect().height) : 0;
      if (measured > 0) {
        this.announcementHeight = measured;
      }
      document.documentElement.style.setProperty('--announcement-height', `${this.announcementHeight}px`);
    }
    setHeaderOffset() {
      let h = this.header_section.getBoundingClientRect().top;
      document.documentElement.style.setProperty('--header-offset', h + 'px');
    }
    setHeaderHeight() {
      const headerEl = this.querySelector('.header');
      const measured = headerEl ? Math.round(headerEl.getBoundingClientRect().height) : Math.round(this.clientHeight);
      if (measured > 0) {
        this.headerHeight = measured;
      }
      document.documentElement.style.setProperty('--header-height', `${this.headerHeight}px`);
    }
    setHeaderGroupHeight() {
      const total = this.headerHeight || 0;
      document.documentElement.style.setProperty('--header-group-height', `${total}px`);
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const isOpen = detailsElement.hasAttribute('open');
      if (isOpen) {
        detailsElement.classList.remove('submenu-open');
      } else {
        detailsElement.classList.add('submenu-open');
      }
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('submenu-open');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}

/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();

      this.submenus = this.querySelectorAll('.thb-full-menu .menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');
    }
    connectedCallback() {
      if (!this.submenus.length) {
        return;
      }
      const _this = this;
      // resize on initial load
      document.fonts.ready.then(function () {
        window.addEventListener('resize', debounce(function () {
          _this.resizeSubMenus();
        }, 100));
        _this.resizeSubMenus();
      });

    }
    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.offsetWidth,
            total = w + l;

          if (total > window.innerWidth) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else if (sub_submenu.parentElement.classList.contains('left-submenu')) {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}
